import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Generic low-level hook: keep a single value in sync with a URL query parameter.
 * - Caller supplies encode/decode so this stays format-agnostic.
 * - Removes param when encode returns null (useful for default values).
 */
export interface UseUrlStateOptions<T> {
  param: string; // query param name
  initial: T; // initial fallback value
  encode: (value: T) => string | null; // return null to remove param entirely
  decode: (raw: string | null) => T; // parse string -> value
  isEqual?: (a: T, b: T) => boolean; // optional equality check (default: Object.is)
}

export const useUrlState = <T>({
  param,
  initial,
  encode,
  decode,
  isEqual,
}: UseUrlStateOptions<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [value, setValueState] = useState<T>(() => {
    const raw = searchParams.get(param);
    return raw != null ? decode(raw) : initial;
  });

  useEffect(() => {
    const raw = searchParams.get(param);
    const next = raw != null ? decode(raw) : initial;
    const eq = isEqual ?? Object.is;
    if (!eq(next, value)) setValueState(next);
  }, [searchParams, param, decode, initial, value, isEqual]);

  const setValue = useCallback(
    (next: T) => {
      setValueState(next);
      const newParams = new URLSearchParams(searchParams.toString());
      const encoded = encode(next);
      if (encoded == null)
        newParams.delete(param); // remove when default / empty
      else newParams.set(param, encoded);
      setSearchParams(newParams, { replace: true });
    },
    [param, encode, searchParams, setSearchParams],
  );

  return { value, setValue } as const;
};
